import React from 'react';
import { Container, Row, Col, Badge, Button, Form, Spinner, Alert } from 'react-bootstrap';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  LockClosedIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { useAdminTicketDetail, statusColors, statusLabels, categoryLabels } from './hooks/useAdminTicketDetail';

const AdminTicketDetail: React.FC = () => {
  const {
    ticket, loading, replyBody, setReplyBody, sending, error,
    messagesEndRef, handleReply, handleClose, handleReopen,
    handleResolve, formatDate, isClosed, navigateToSupport,
  } = useAdminTicketDetail();

  if (loading) {
    return (
      <>
        <Header />
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      </>
    );
  }

  if (!ticket) {
    return (
      <>
        <Header />
        <Container className="py-5 text-center">
          <ExclamationCircleIcon style={{ width: 48, height: 48, color: '#dc2626', marginBottom: 12 }} />
          <p style={{ color: '#64748b' }}>Ticket not found.</p>
          <Button variant="outline-secondary" onClick={navigateToSupport}>Back to Tickets</Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container fluid className="px-3 px-lg-4 page-content-padding" style={{ paddingTop: '24px' }}>
        {/* Back + Header */}
        <Button
          variant="link"
          className="d-flex align-items-center gap-1 p-0 mb-3"
          style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 500 }}
          onClick={navigateToSupport}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Back to tickets
        </Button>

        <Row className="g-4">
          {/* Main conversation area */}
          <Col lg={8}>
            {/* Subject header */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '20px',
              marginBottom: '16px',
            }}>
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                <div>
                  <h5 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>{ticket.subject}</h5>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span style={{
                      background: statusColors[ticket.status]?.bg,
                      color: statusColors[ticket.status]?.text,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      padding: '4px 10px',
                      borderRadius: '6px',
                    }}>
                      {statusLabels[ticket.status]}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{ticket.ticket_id}</span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>•</span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{categoryLabels[ticket.category]}</span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>•</span>
                    <span style={{
                      background: '#e0e7ff',
                      color: '#3730a3',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      padding: '3px 8px',
                      borderRadius: '4px',
                    }}>
                      {ticket.company_name}
                    </span>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  {isClosed ? (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="d-flex align-items-center gap-1"
                      style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem' }}
                      onClick={handleReopen}
                    >
                      <ArrowPathIcon style={{ width: '14px', height: '14px' }} />
                      Reopen
                    </Button>
                  ) : (
                    <>
                      {ticket.status !== 'resolved' && (
                        <Button
                          size="sm"
                          variant="outline-success"
                          className="d-flex align-items-center gap-1"
                          style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem' }}
                          onClick={handleResolve}
                        >
                          <CheckCircleIcon style={{ width: '14px', height: '14px' }} />
                          Resolve
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="d-flex align-items-center gap-1"
                        style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem' }}
                        onClick={handleClose}
                      >
                        <LockClosedIcon style={{ width: '14px', height: '14px' }} />
                        Close
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {error && <Alert variant="danger" className="py-2" style={{ fontSize: '0.85rem' }}>{error}</Alert>}

            {/* Messages */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
            }}>
              {ticket.messages.map((msg, idx) => (
                <div
                  key={msg.id}
                  style={{
                    padding: '20px',
                    borderBottom: idx < ticket.messages.length - 1 ? '1px solid #f1f5f9' : 'none',
                    background: msg.is_admin_reply ? '#f5f3ff' : 'transparent',
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: msg.is_admin_reply
                        ? 'linear-gradient(135deg, #6d28d9, #8b5cf6)'
                        : 'linear-gradient(135deg, #065f46, #059669)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 700, fontSize: '0.75rem',
                      flexShrink: 0,
                    }}>
                      {msg.is_admin_reply ? 'A' : msg.sender_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>
                        {msg.sender_name}
                      </span>
                      {msg.is_admin_reply && (
                        <span style={{
                          background: '#ede9fe',
                          color: '#6d28d9',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          marginLeft: '6px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                        }}>
                          Admin
                        </span>
                      )}
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                        {formatDate(msg.created_at)}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    paddingLeft: '44px',
                    fontSize: '0.9rem',
                    color: '#374151',
                    lineHeight: 1.65,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {msg.body}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply form */}
            {!isClosed ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '20px',
                marginTop: '16px',
              }}>
                <Form onSubmit={handleReply}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>
                      <ChatBubbleLeftRightIcon style={{ width: '16px', height: '16px', marginRight: '6px', color: '#6d28d9' }} />
                      Admin Reply
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={replyBody}
                      onChange={e => setReplyBody(e.target.value)}
                      placeholder="Type your response to the company..."
                      required
                      style={{ borderColor: '#e2e8f0', borderRadius: '10px', fontSize: '0.9rem', resize: 'vertical' }}
                    />
                  </Form.Group>
                  <div className="d-flex justify-content-end">
                    <Button
                      type="submit"
                      disabled={sending || !replyBody.trim()}
                      className="d-flex align-items-center gap-2"
                      style={{
                        borderRadius: '10px',
                        padding: '10px 24px',
                        fontWeight: 600,
                        fontSize: '0.88rem',
                        background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)',
                        color: 'white',
                        border: 'none',
                      }}
                    >
                      <PaperAirplaneIcon style={{ width: '16px', height: '16px' }} />
                      {sending ? 'Sending...' : 'Send Reply'}
                    </Button>
                  </div>
                </Form>
              </div>
            ) : (
              <div style={{
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '20px',
                marginTop: '16px',
                textAlign: 'center',
              }}>
                <LockClosedIcon style={{ width: '20px', height: '20px', color: '#94a3b8', marginBottom: '6px' }} />
                <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '8px' }}>
                  This ticket is {ticket.status}. Reopen it to reply.
                </p>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={handleReopen}
                  style={{ borderRadius: '8px', fontWeight: 600 }}
                >
                  <ArrowPathIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                  Reopen Ticket
                </Button>
              </div>
            )}
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '20px',
            }}>
              <h6 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '16px', fontSize: '0.92rem' }}>
                Ticket Details
              </h6>
              {[
                { label: 'Ticket ID', value: ticket.ticket_id },
                { label: 'Company', value: ticket.company_name },
                { label: 'Status', value: statusLabels[ticket.status], badge: true, badgeColor: statusColors[ticket.status] },
                { label: 'Priority', value: ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1) },
                { label: 'Category', value: categoryLabels[ticket.category] },
                { label: 'Created', value: formatDate(ticket.created_at) },
                { label: 'Last Updated', value: formatDate(ticket.updated_at) },
                { label: 'Messages', value: String(ticket.message_count) },
              ].map((item, i) => (
                <div key={item.label} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: i < 7 ? '1px solid #f1f5f9' : 'none',
                }}>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{item.label}</span>
                  {item.badge ? (
                    <span style={{
                      background: item.badgeColor?.bg,
                      color: item.badgeColor?.text,
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      padding: '3px 8px',
                      borderRadius: '6px',
                    }}>
                      {item.value}
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.82rem', color: '#0f172a', fontWeight: 500 }}>{item.value}</span>
                  )}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Container>
      <Footer />
    </>
  );
};

export default AdminTicketDetail;
